import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ThemeProvider } from './context/ThemeContext';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/common/ProtectedRoute';
import Background3D from './components/Background3D';


// Core Pages (Static Import for Stability)
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Auth from './pages/Auth';
import About from './pages/About';

// Policies (Static Import)
import ShippingPolicy from './pages/ShippingPolicy';
import ReturnPolicy from './pages/ReturnPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';

// Lazy Load Pages (Secondary)
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const SizeChart = lazy(() => import('./pages/SizeChart'));

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminProducts = lazy(() => import('./pages/admin/ProductsManagement'));
const AdminCategories = lazy(() => import('./pages/admin/CategoriesManagement'));
const AdminOrders = lazy(() => import('./pages/admin/OrdersManagement'));
const AdminCustomization = lazy(() => import('./pages/admin/Customization'));
const AdminMarketing = lazy(() => import('./pages/admin/MarketingManagement'));
const AdminSettings = lazy(() => import('./pages/admin/SettingsManagement'));
const AdminCoupons = lazy(() => import('./pages/admin/Coupons'));

import ErrorBoundary from './components/common/ErrorBoundary';
import RandomPurchasePopup from './components/RandomPurchasePopup';

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <CartProvider>
          <Router>
              <ScrollToTop />
              <Background3D />
              <div className="min-h-screen flex flex-col bg-transparent text-white transition-colors duration-200">


                <Header />
                <main className="flex-grow">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/products/:id" element={<ProductDetail />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/auth" element={<Auth />} />

                    {/* Wrap Lazy routes in individual Suspense for better isolation */}
                    <Route path="/about" element={<About />} />
                    <Route path="/shipping" element={<ShippingPolicy />} />
                    <Route path="/returns" element={<ReturnPolicy />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/size-chart" element={<Suspense fallback={<LoadingSpinner />}><SizeChart /></Suspense>} />

                    {/* Protected Routes */}
                    <Route
                      path="/checkout"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <Checkout />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/order/:id"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <OrderConfirmation />
                        </Suspense>
                      }
                    />
                    {/* Admin Routes */}
                    <Route
                      path="/admin/dashboard"
                      element={
                        <ProtectedRoute adminOnly>
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminDashboard />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/products"
                      element={
                        <ProtectedRoute adminOnly>
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminProducts />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/categories"
                      element={
                        <ProtectedRoute adminOnly>
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminCategories />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/orders"
                      element={
                        <ProtectedRoute adminOnly>
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminOrders />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/customization"
                      element={
                        <ProtectedRoute adminOnly>
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminCustomization />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/marketing"
                      element={
                        <ProtectedRoute adminOnly>
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminMarketing />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings"
                      element={
                        <ProtectedRoute adminOnly>
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminSettings />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/coupons"
                      element={
                        <ProtectedRoute adminOnly>
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminCoupons />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </main>
                <Footer />
              </div>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 3000,
                  style: {
                    background: 'var(--toast-bg)',
                    color: 'var(--toast-color)',
                  },
                }}
              />
              <RandomPurchasePopup />
            </Router>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
